import { useState, useEffect } from 'react';
import Switch from 'react-switch';
import { getRules, createRule, updateRule, deleteRule } from './utils';

const RULE_TYPES = ['modes', 'agencies', 'parkingCost', 'subsidy'];

const DIRECTIONS = [
    { value: '', label: 'Both (internal & external)' },
    { value: 'internal', label: 'Internal (departing)' },
    { value: 'external', label: 'External (arriving)' },
];

const ALL_MODES = ['walk', 'bus', 'subway', 'train', 'tram', 'ferry', 'bike', 'bikeshare', 'car', 'rideHailing', 'scooter', 'shuttle'];
const ALL_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const INTERVALS = ['', 'hourly', 'daily', 'weekly', 'monthly'];

const EMPTY_RULE = {
    type: 'modes',
    direction: null,
    config: { value: [] },
    interval: null,
    relevantModes: null,
    isActive: true,
    timeFrom: null,
    timeTo: null,
    days: null,
    dateFrom: null,
    dateTo: null,
};

const SublocationRules = ({ sublocationId }) => {
    const [rules, setRules] = useState([]);
    const [editRule, setEditRule] = useState(null);
    const [isNewRule, setIsNewRule] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (sublocationId) fetchRules();
    }, [sublocationId]);

    const fetchRules = async () => {
        const data = await getRules(sublocationId);
        if (Array.isArray(data)) setRules(data);
    };

    const handleAddRule = () => {
        setEditRule({ ...EMPTY_RULE, sublocationId });
        setIsNewRule(true);
    };

    const handleEditRule = (rule) => {
        setEditRule({ ...rule });
        setIsNewRule(false);
    };

    const handleCancelEdit = () => {
        setEditRule(null);
        setIsNewRule(false);
    };

    const handleToggleActive = async (rule) => {
        const updated = { ...rule, isActive: !rule.isActive };
        await updateRule(updated);
        setRules(prev => prev.map(r => r.id === rule.id ? updated : r));
    };

    const handleDeleteRule = async (id) => {
        await deleteRule(id);
        setRules(prev => prev.filter(r => r.id !== id));
        if (editRule?.id === id) setEditRule(null);
    };

    const handleSaveRule = async () => {
        setLoading(true);
        try {
            if (isNewRule) {
                const created = await createRule(editRule);
                if (created?.id) {
                    setRules(prev => [...prev, created]);
                    setEditRule(null);
                    setIsNewRule(false);
                }
            } else {
                await updateRule(editRule);
                setRules(prev => prev.map(r => r.id === editRule.id ? editRule : r));
                setEditRule(null);
            }
        } catch (e) {
            console.error('Failed to save rule', e);
        }
        setLoading(false);
    };

    const handleRuleFieldChange = (field, value) => {
        setEditRule(prev => ({ ...prev, [field]: value }));
    };

    const handleConfigValueChange = (value) => {
        setEditRule(prev => ({ ...prev, config: { ...prev.config, value } }));
    };

    const handleModeToggle = (mode) => {
        const current = Array.isArray(editRule.config?.value) ? editRule.config.value : [];
        const updated = current.includes(mode) ? current.filter(m => m !== mode) : [...current, mode];
        handleConfigValueChange(updated);
    };

    const handleDayToggle = (day) => {
        const current = editRule.days || [];
        const updated = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
        handleRuleFieldChange('days', updated.length ? updated : null);
    };

    const handleRelevantModeToggle = (mode) => {
        const current = editRule.relevantModes || [];
        const updated = current.includes(mode) ? current.filter(m => m !== mode) : [...current, mode];
        handleRuleFieldChange('relevantModes', updated.length ? updated : null);
    };

    const formatConfigValue = (rule) => {
        if (!rule.config?.value && rule.config?.value !== 0) return '—';
        if (Array.isArray(rule.config.value)) return rule.config.value.join(', ') || '—';
        return String(rule.config.value);
    };

    const formatSchedule = (rule) => {
        const parts = [];
        if (rule.days?.length) parts.push(rule.days.map(d => d.slice(0, 3)).join(', '));
        if (rule.timeFrom || rule.timeTo) parts.push(`${rule.timeFrom || ''}–${rule.timeTo || ''}`);
        if (rule.dateFrom || rule.dateTo) parts.push(`${rule.dateFrom || ''}→${rule.dateTo || ''}`);
        return parts.join(' | ') || '—';
    };

    return (
        <div className="sublocation-rules-container">
            <div className="rules-header">
                <h3>Rules</h3>
                <button className="add-rule-btn" onClick={handleAddRule}>+ Add Rule</button>
            </div>

            {rules.length === 0 && !isNewRule
                ? <p className="no-rules-text">No rules configured for this sublocation.</p>
                : <div className="locations-table-wrapper">
                    <table className="rules-table locations-table">
                        <thead>
                            <tr className="header-row">
                                <th className="table-header">Type</th>
                                <th className="table-header">Direction</th>
                                <th className="table-header">Value</th>
                                <th className="table-header">Schedule</th>
                                <th className="table-header">Active</th>
                                <th className="table-header">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="table-body">
                            {rules.map(rule => (
                                <tr key={rule.id} className={`location-row ${editRule?.id === rule.id ? 'selected' : ''}`}>
                                    <td>{rule.type}</td>
                                    <td>{rule.direction || 'both'}</td>
                                    <td>{formatConfigValue(rule)}</td>
                                    <td>{formatSchedule(rule)}</td>
                                    <td>
                                        <Switch
                                            checked={rule.isActive}
                                            onChange={() => handleToggleActive(rule)}
                                            onColor="#2386EE"
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            height={16}
                                            width={36}
                                        />
                                    </td>
                                    <td>
                                        <button className="rule-action-btn" onClick={() => handleEditRule(rule)}>Edit</button>
                                        <button className="rule-action-btn rule-action-btn--delete" onClick={() => handleDeleteRule(rule.id)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }

            {editRule && (
                <RuleForm
                    rule={editRule}
                    isNew={isNewRule}
                    onFieldChange={handleRuleFieldChange}
                    onConfigValueChange={handleConfigValueChange}
                    onModeToggle={handleModeToggle}
                    onDayToggle={handleDayToggle}
                    onRelevantModeToggle={handleRelevantModeToggle}
                    onSave={handleSaveRule}
                    onCancel={handleCancelEdit}
                    loading={loading}
                />
            )}
        </div>
    );
};

const RuleForm = ({ rule, isNew, onFieldChange, onConfigValueChange, onModeToggle, onDayToggle, onRelevantModeToggle, onSave, onCancel, loading }) => {
    const isNumericType = rule.type === 'parkingCost' || rule.type === 'subsidy';

    return (
        <div className="rule-form">
            <h4>{isNew ? 'New Rule' : 'Edit Rule'}</h4>
            <div className="rule-form-body">
                <div className="rule-form-left">

                    <div className="input-container">
                        <div className="label">Type:</div>
                        <select className="input" value={rule.type} onChange={e => {
                            const newType = e.target.value;
                            const isArr = newType === 'modes' || newType === 'agencies';
                            onFieldChange('type', newType);
                            onConfigValueChange(isArr ? [] : '');
                        }}>
                            {RULE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>

                    <div className="input-container">
                        <div className="label">Direction:</div>
                        <select className="input" value={rule.direction ?? ''} onChange={e => onFieldChange('direction', e.target.value || null)}>
                            {DIRECTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                    </div>

                    {rule.type === 'modes' && (
                        <div className="input-container input-container--column">
                            <div className="label">Modes:</div>
                            <div className="checkbox-group">
                                {ALL_MODES.map(mode => (
                                    <label key={mode} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={(rule.config?.value || []).includes(mode)}
                                            onChange={() => onModeToggle(mode)}
                                        />
                                        {mode}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {rule.type === 'agencies' && (
                        <div className="input-container">
                            <div className="label">Agencies:</div>
                            <input
                                className="input"
                                type="text"
                                placeholder="Comma-separated agency names"
                                value={Array.isArray(rule.config?.value) ? rule.config.value.join(', ') : ''}
                                onChange={e => onConfigValueChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                            />
                        </div>
                    )}

                    {isNumericType && (
                        <div className="input-container">
                            <div className="label">Value ($):</div>
                            <input
                                className="input"
                                type="number"
                                step="0.01"
                                min="0"
                                value={rule.config?.value ?? ''}
                                onChange={e => onConfigValueChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
                            />
                        </div>
                    )}

                    {isNumericType && (
                        <div className="input-container">
                            <div className="label">Interval:</div>
                            <select className="input" value={rule.interval ?? ''} onChange={e => onFieldChange('interval', e.target.value || null)}>
                                {INTERVALS.map(i => <option key={i} value={i}>{i || 'None'}</option>)}
                            </select>
                        </div>
                    )}

                    {rule.type === 'subsidy' && (
                        <div className="input-container input-container--column">
                            <div className="label">Relevant Modes (leave blank for all):</div>
                            <div className="checkbox-group">
                                {ALL_MODES.map(mode => (
                                    <label key={mode} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={(rule.relevantModes || []).includes(mode)}
                                            onChange={() => onRelevantModeToggle(mode)}
                                        />
                                        {mode}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="input-container">
                        <div className="label">Active:</div>
                        <Switch
                            checked={rule.isActive}
                            onChange={val => onFieldChange('isActive', val)}
                            onColor="#2386EE"
                            uncheckedIcon={false}
                            checkedIcon={false}
                            height={20}
                            width={45}
                        />
                    </div>
                </div>

                <div className="rule-form-right">
                    <div className="input-container input-container--column">
                        <div className="label">Days (leave blank for all):</div>
                        <div className="checkbox-group">
                            {ALL_DAYS.map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={(rule.days || []).includes(day)}
                                        onChange={() => onDayToggle(day)}
                                    />
                                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="input-container">
                        <div className="label">Time from:</div>
                        <input
                            className="input"
                            type="time"
                            value={rule.timeFrom ?? ''}
                            onChange={e => onFieldChange('timeFrom', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Time to:</div>
                        <input
                            className="input"
                            type="time"
                            value={rule.timeTo ?? ''}
                            onChange={e => onFieldChange('timeTo', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Date from:</div>
                        <input
                            className="input"
                            type="date"
                            value={rule.dateFrom ?? ''}
                            onChange={e => onFieldChange('dateFrom', e.target.value || null)}
                        />
                    </div>

                    <div className="input-container">
                        <div className="label">Date to:</div>
                        <input
                            className="input"
                            type="date"
                            value={rule.dateTo ?? ''}
                            onChange={e => onFieldChange('dateTo', e.target.value || null)}
                        />
                    </div>
                </div>
            </div>

            <div className="rule-form-actions">
                <button className="save-rule-btn" onClick={onSave} disabled={loading}>
                    {loading ? 'Saving…' : isNew ? 'Create Rule' : 'Save Rule'}
                </button>
                <button className="cancel-rule-btn" onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
};

export default SublocationRules;
